/**
 * Response Code Type Definitions
 *
 * CODE-ONLY PATTERN:
 * Backend sends response codes WITHOUT messages to minimize payload size.
 * Frontend maintains a separate code-to-message mapping in response-codes.ts.
 *
 * SYNCHRONIZATION REQUIREMENT:
 * This enum MUST stay synchronized with backend codes.
 * When adding/removing codes, update BOTH:
 *   - Backend: apps/consumer/processors/responses/codes.go
 *   - Frontend: This file
 *
 * Keep alphabetically sorted with NO logical grouping.
 */

/**
 * Standardized response codes used across the application.
 * IMPORTANT: Keep alphabetically sorted and synced with backend
 * - Backend Consumer: apps/consumer/processors/responses/codes.go
 */
export enum ResponseCode {
  ACCEPTED = 'ACCEPTED',
  ACTION_NOT_SUPPORTED = 'ACTION_NOT_SUPPORTED',
  ASYNC_OPERATION_QUEUED = 'ASYNC_OPERATION_QUEUED',
  AUTH_CONTEXT_FAILED = 'AUTH_CONTEXT_FAILED',
  BOARD_NOT_DELETABLE = 'BOARD_NOT_DELETABLE',
  BOARD_TAG_LIMIT_REACHED = 'BOARD_TAG_LIMIT_REACHED',
  BOARD_TAG_NOT_ALLOWED_FOR_GROUP = 'BOARD_TAG_NOT_ALLOWED_FOR_GROUP',
  BUSINESS_RULE_VIOLATION = 'BUSINESS_RULE_VIOLATION',
  CARD_EDIT_LOCKED = 'CARD_EDIT_LOCKED',
  CARD_FROZEN = 'CARD_FROZEN',
  COLUMN_CAPACITY_EXCEEDED = 'COLUMN_CAPACITY_EXCEEDED',
  CONFIRM_ON_EXIT_FIELD_REQUIRED = 'CONFIRM_ON_EXIT_FIELD_REQUIRED',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  CONFLICT = 'CONFLICT',
  CREATED = 'CREATED',
  DATABASE_ERROR = 'DATABASE_ERROR',
  DEPENDENCY_MISSING = 'DEPENDENCY_MISSING',
  DUPLICATE_RESOURCE = 'DUPLICATE_RESOURCE',
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  FACTORY_ERROR = 'FACTORY_ERROR',
  FORBIDDEN = 'FORBIDDEN',
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  JOB_ALREADY_EXISTS = 'JOB_ALREADY_EXISTS',
  JOB_ALREADY_RUNNING = 'JOB_ALREADY_RUNNING',
  JOB_NOT_FOUND = 'JOB_NOT_FOUND',
  JOB_RUN_NOT_FOUND = 'JOB_RUN_NOT_FOUND',
  INVALID_CARD_MOVEMENT = 'INVALID_CARD_MOVEMENT',
  INVALID_INPUT = 'INVALID_INPUT',
  INVALID_JSON = 'INVALID_JSON',
  INVALID_STATUS = 'INVALID_STATUS',
  IN_PROGRESS_LIMIT_EXCEEDED = 'IN_PROGRESS_LIMIT_EXCEEDED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  NETWORK_ERROR = 'NETWORK_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  NO_CONTENT = 'NO_CONTENT',
  OPERATION_FAILED = 'OPERATION_FAILED',
  OPERATION_NOT_ALLOWED = 'OPERATION_NOT_ALLOWED',
  ORGANIZATION_CODE_ALREADY_EXISTS = 'ORGANIZATION_CODE_ALREADY_EXISTS',
  ORGANIZATION_TITLE_ALREADY_EXISTS = 'ORGANIZATION_TITLE_ALREADY_EXISTS',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  PASSWORD_RESET_FAILED = 'PASSWORD_RESET_FAILED',
  PASSWORD_TOO_SHORT = 'PASSWORD_TOO_SHORT',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  PROCESSOR_NOT_FOUND = 'PROCESSOR_NOT_FOUND',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  RATE_LIMITED = 'RATE_LIMITED',
  REQUEST_NOT_FOUND = 'REQUEST_NOT_FOUND',
  RESOURCE_LOCKED = 'RESOURCE_LOCKED',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  START_DATE_IMMUTABLE = 'START_DATE_IMMUTABLE',
  STUDENT_BACKWARD_MOVEMENT_BLOCKED = 'STUDENT_BACKWARD_MOVEMENT_BLOCKED',
  SUCCESS = 'SUCCESS',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  USERNAME_ALREADY_EXISTS = 'USERNAME_ALREADY_EXISTS',
  USER_NOT_AUTHENTICATED = 'USER_NOT_AUTHENTICATED',
  USER_NOT_AUTHORIZED = 'USER_NOT_AUTHORIZED',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
}
